import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Users, CalendarDays, CreditCard, Check, Loader2, CheckCircle, Shield, FileText, Mail, Clock, DollarSign, Phone, MessageCircle, AlertCircle } from "lucide-react";
import SEO from "@/components/SEO";
import axios from "axios";
import { format, differenceInDays, addDays, isBefore, isAfter, isSameDay, parseISO } from "date-fns";
import RentalAgreement from "@/components/RentalAgreement";
import { PetPolicyDialog } from "@/components/PetPolicyDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios with timeout to prevent hanging requests
axios.defaults.timeout = 30000; // 30 second timeout

// Error tracking - log errors to backend
const logError = async (error, context) => {
  try {
    await axios.post(`${API}/log-error`, {
      error: error?.message || String(error),
      stack: error?.stack,
      context,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.error("Failed to log error:", e);
  }
};

export default function BookingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const existingBookingId = searchParams.get("booking_id");

  const [step, setStep] = useState(1);
  const [property, setProperty] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [earlyCheckinBuffers, setEarlyCheckinBuffers] = useState([]); // Early check-in buffer days
  const [lateCheckoutBuffers, setLateCheckoutBuffers] = useState([]); // Late checkout buffer days
  const [earlyCheckinDays, setEarlyCheckinDays] = useState([]); // Early check-in days (fully red)
  const [lateCheckoutDays, setLateCheckoutDays] = useState([]); // Late checkout days (fully red)
  const [checkoutDates, setCheckoutDates] = useState([]); // Regular checkout days from all bookings
  const [checkinDates, setCheckinDates] = useState([]); // Regular check-in days from all bookings
  const [loading, setLoading] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);

  // Booking form state
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [guestCount, setGuestCount] = useState(2);
  const [discountCode, setDiscountCode] = useState("");
  const [discountApplied, setDiscountApplied] = useState(false);
  const [protectionType, setProtectionType] = useState("deposit"); // "deposit" or "insurance"
  const [paymentMethod, setPaymentMethod] = useState("stripe"); // "stripe", "venmo", "cashapp", "zelle", "check"
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [guestInfo, setGuestInfo] = useState({
    name: "",
    email: "",
    phone: "",
    specialRequests: "",
    marketingOptIn: false,
  });
  const [pricing, setPricing] = useState(null);
  const [booking, setBooking] = useState(null);
  
  // Add-ons state
  const [addons, setAddons] = useState([]);
  const [addonAvailability, setAddonAvailability] = useState({});
  const [selectedAddons, setSelectedAddons] = useState({});
  const [addonQuantities, setAddonQuantities] = useState({});
  // Pets
  const [petCount, setPetCount] = useState(0);
  const [petPolicyAgreed, setPetPolicyAgreed] = useState(false);
  const [showPetPolicy, setShowPetPolicy] = useState(false);
  const [pendingPetCount, setPendingPetCount] = useState(0);
  
  // Agreement state
  const [agreedToRules, setAgreedToRules] = useState(false);
  const [agreementSigned, setAgreementSigned] = useState(false);

  useEffect(() => {
    fetchProperty();
    fetchBlockedDates();
    fetchAddons();
    fetchPaymentMethods();
    if (existingBookingId) {
      fetchExistingBooking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingBookingId]);

  const fetchProperty = async () => {
    try {
      const res = await axios.get(`${API}/property`);
      setProperty(res.data);
    } catch (e) {
      console.error("Error:", e);
      logError(e, "fetchProperty");
    }
  };

  const fetchBlockedDates = async () => {
    try {
      const res = await axios.get(`${API}/blocked-dates`);
      const blocked = res.data.blocked || [];
      const booked = res.data.booked || [];
      const bookings = res.data.bookings || []; // Get booking info with checkout dates
      
      // Separate different types of buffer days
      const earlyBuffers = blocked.filter(d => d.source?.startsWith('buffer_early_'));
      const lateBuffers = blocked.filter(d => d.source?.startsWith('buffer_late_'));
      const lateCheckoutDays = blocked.filter(d => d.source?.startsWith('latecheckout_day_'));
      const nonBufferBlocked = blocked.filter(d => !d.source?.startsWith('buffer_') && !d.source?.startsWith('latecheckout_day_'));
      
      // Store buffer days separately by type
      setEarlyCheckinBuffers(earlyBuffers.map(d => parseISO(d.date)));
      setLateCheckoutBuffers(lateBuffers.map(d => parseISO(d.date)));
      
      // Find bookings with early check-in add-ons (their check-in day should be fully RED)
      const earlyCheckinDayDates = bookings
        .filter(b => {
          const addons = b.selected_addons || [];
          return addons.some(a => a.name?.toLowerCase().includes('early check-in') || a.name?.toLowerCase().includes('early check in'));
        })
        .map(b => parseISO(b.check_in));
      setEarlyCheckinDays(earlyCheckinDayDates);
      
      // Find bookings with late checkout add-ons (for reference)
      const lateCheckoutBookingCheckouts = bookings
        .filter(b => {
          const addons = b.selected_addons || [];
          return addons.some(a => a.name?.toLowerCase().includes('late check-out') || a.name?.toLowerCase().includes('late checkout'));
        })
        .map(b => parseISO(b.check_out));
      setLateCheckoutDays(lateCheckoutBookingCheckouts);
      
      // Extract checkout dates from bookings (but not if it's a late checkout day - those should be fully blocked)
      // Use lateCheckoutBookingCheckouts directly (not state) since setState is async
      const checkouts = bookings
        .filter(b => b.check_out)
        .map(b => parseISO(b.check_out))
        .filter(checkoutDate => {
          // Don't mark as regular checkout if it's a late checkout day (should be fully red)
          const isLateCheckoutDay = lateCheckoutBookingCheckouts.some(lcd => isSameDay(lcd, checkoutDate));
          return !isLateCheckoutDay;
        });
      setCheckoutDates(checkouts);
      
      // Extract check-in dates from bookings - but NOT early check-in days (those should be fully red)
      const checkins = bookings
        .filter(b => b.check_in)
        .map(b => parseISO(b.check_in))
        .filter(checkinDate => {
          // Don't mark as half/half check-in if it's an early check-in day (should be fully red)
          return !earlyCheckinDayDates.some(ecd => isSameDay(ecd, checkinDate));
        });
      setCheckinDates(checkins);
      
      // Fully blocked days: booked nights + manually blocked + late checkout days + early check-in days
      const earlyCheckinDayObjects = earlyCheckinDayDates.map(d => ({ date: format(d, 'yyyy-MM-dd') }));
      const lateCheckoutDayObjects = lateCheckoutBookingCheckouts.map(d => ({ date: format(d, 'yyyy-MM-dd') }));
      const allBlocked = [
        ...nonBufferBlocked, 
        ...booked, 
        ...lateCheckoutDays,
        ...earlyCheckinDayObjects,
        ...lateCheckoutDayObjects
      ].map((d) => parseISO(d.date));
      setBlockedDates(allBlocked);
    } catch (e) {
      console.error("Error:", e);
      logError(e, "fetchBlockedDates");
    }
  };

  const isDateEarlyCheckinBuffer = (date) => {
    return earlyCheckinBuffers.some((buffer) => isSameDay(buffer, date));
  };

  const isDateLateCheckoutBuffer = (date) => {
    return lateCheckoutBuffers.some((buffer) => isSameDay(buffer, date));
  };

  const isDateEarlyCheckin = (date) => {
    return earlyCheckinDays.some((earlyCheckin) => isSameDay(earlyCheckin, date));
  };

  const isDateLateCheckout = (date) => {
    return lateCheckoutDays.some((lateCheckout) => isSameDay(lateCheckout, date));
  };

  const isDateCheckout = (date) => {
    return checkoutDates.some((checkout) => isSameDay(checkout, date));
  };

  const isDateCheckin = (date) => {
    return checkinDates.some((checkin) => isSameDay(checkin, date));
  };

  const fetchAddons = async () => {
    try {
      const res = await axios.get(`${API}/addons`);
      setAddons(res.data || []);
    } catch (e) {
      console.error("Error fetching addons:", e);
      logError(e, "fetchAddons");
    }
  };

  const checkAddonAvailability = async (checkIn, checkOut) => {
    try {
      const res = await axios.get(`${API}/addons/availability`, {
        params: {
          check_in: checkIn,
          check_out: checkOut,
        },
      });
      setAddonAvailability(res.data);
    } catch (e) {
      console.error("Error checking addon availability:", e);
      logError(e, "checkAddonAvailability");
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const res = await axios.get(`${API}/payment-methods`);
      setPaymentMethods(res.data.methods || []);
    } catch (e) {
      console.error("Error fetching payment methods:", e);
      logError(e, "fetchPaymentMethods");
    }
  };

  const fetchExistingBooking = async () => {
    try {
      const res = await axios.get(`${API}/bookings/${existingBookingId}`);
      setBooking(res.data);
      setAgreementSigned(true); // Already agreed if coming back to an existing booking
      setStep(4); // Go directly to payment step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      console.error("Error:", e);
      logError(e, "fetchExistingBooking");
    }
  };

  const isDateBlocked = (date) => {
    return blockedDates.some((blocked) => isSameDay(blocked, date));
  };

  const handleDateSelect = async (range) => {
    setDateRange(range);
    if (range?.from && range?.to) {
      const checkIn = format(range.from, "yyyy-MM-dd");
      const checkOut = format(range.to, "yyyy-MM-dd");
      
      try {
        const params = {
          check_in: checkIn,
          check_out: checkOut,
          protection_type: protectionType,
        };
        if (discountCode) {
          params.discount_code = discountCode;
        }
        const res = await axios.get(`${API}/availability`, { params });
        if (res.data.available) {
          setPricing(res.data);
          if (res.data.discount_applied) {
            setDiscountApplied(true);
            toast.success(`Discount code applied! ${(res.data.discount_percent * 100).toFixed(0)}% off`);
          }
          // Check addon availability for selected dates
          checkAddonAvailability(checkIn, checkOut);
        } else {
          toast.error(res.data.reason || "Dates not available");
          setDateRange({ from: undefined, to: undefined });
        }
      } catch (e) {
        const isTimeout = e.code === 'ECONNABORTED' || e.message?.includes('timeout');
        const errorMsg = isTimeout 
          ? "Request timed out. Please check your internet connection and try again."
          : "Error checking availability. Please try again.";
        toast.error(errorMsg);
        logError(e, "handleDateSelect_availability");
      }
    }
  };

  const handleProtectionChange = async (value) => {
    setProtectionType(value);
    if (dateRange?.from && dateRange?.to) {
      try {
        const params = {
          check_in: format(dateRange.from, "yyyy-MM-dd"),
          check_out: format(dateRange.to, "yyyy-MM-dd"),
          protection_type: value,
        };
        if (discountCode && discountApplied) {
          params.discount_code = discountCode;
        }
        const res = await axios.get(`${API}/availability`, { params });
        if (res.data.available) {
          setPricing(res.data);
        }
      } catch (e) {
        console.error("Error updating pricing:", e);
        logError(e, "handleProtectionChange");
      }
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode || !dateRange?.from || !dateRange?.to) {
      toast.error("Please select dates first");
      return;
    }
    try {
      const res = await axios.get(`${API}/availability`, {
        params: {
          check_in: format(dateRange.from, "yyyy-MM-dd"),
          check_out: format(dateRange.to, "yyyy-MM-dd"),
          discount_code: discountCode,
          protection_type: protectionType,
        },
      });
      if (res.data.available) {
        setPricing(res.data);
        if (res.data.discount_applied) {
          setDiscountApplied(true);
          toast.success(`Discount code applied! ${(res.data.discount_percent * 100).toFixed(0)}% off`);
        } else {
          toast.error("Invalid discount code");
          setDiscountApplied(false);
        }
      }
    } catch (e) {
      toast.error("Error applying discount");
      logError(e, "handleApplyDiscount");
    }
  };

  const handleContinueToGuests = () => {
    if (!dateRange?.from || !dateRange?.to) {
      toast.error("Please select your dates");
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCreateBooking = async () => {
    if (!guestInfo.name || !guestInfo.email || !guestInfo.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        check_in: format(dateRange.from, "yyyy-MM-dd"),
        check_out: format(dateRange.to, "yyyy-MM-dd"),
        guest_count: guestCount,
        guest_name: guestInfo.name,
        guest_email: guestInfo.email,
        guest_phone: guestInfo.phone,
        special_requests: guestInfo.specialRequests || null,
        protection_type: protectionType,
        payment_method: "stripe",  // First payment must be credit card
        discount_code: discountApplied ? discountCode : null,
        selected_addons: Object.keys(selectedAddons).filter(id => selectedAddons[id]),
        addon_quantities: addonQuantities,
        marketing_opt_in: guestInfo.marketingOptIn || false,
      });
      setBooking(res.data);
      setStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success("Booking created! Proceed to payment.");
    } catch (e) {
      const isTimeout = e.code === 'ECONNABORTED' || e.message?.includes('timeout');
      const isNetworkError = e.message === 'Network Error' || !e.response;
      
      let errorMsg;
      if (isTimeout) {
        errorMsg = "Request timed out. Please check your internet connection and try again.";
      } else if (isNetworkError) {
        errorMsg = "Unable to connect to server. Please check your internet and try again, or use 'Request to Book' below.";
      } else {
        const errorDetail = e.response?.data?.detail;
        errorMsg = typeof errorDetail === 'string' ? errorDetail : 
                   Array.isArray(errorDetail) ? errorDetail[0]?.msg || "Error creating booking" :
                   "Error creating booking. Please try again or use 'Request to Book' below.";
      }
      toast.error(errorMsg);
      logError(e, "handleCreateBooking");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (paymentNumber = 1) => {
    if (!booking) return;
    
    setCheckingOut(true);
    try {
      const res = await axios.post(`${API}/checkout/create-session`, {
        booking_id: booking.id,
        origin_url: window.location.origin,
        payment_number: paymentNumber,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      const isTimeout = e.code === 'ECONNABORTED' || e.message?.includes('timeout');
      const isNetworkError = e.message === 'Network Error' || !e.response;
      
      let errorMsg;
      if (isTimeout) {
        errorMsg = "Request timed out. Please check your internet connection and try again.";
      } else if (isNetworkError) {
        errorMsg = "Unable to connect to payment server. Please check your internet and try again.";
      } else {
        const errorDetail = e.response?.data?.detail;
        errorMsg = typeof errorDetail === 'string' ? errorDetail : 
                   Array.isArray(errorDetail) ? errorDetail[0]?.msg || "Error creating checkout session" :
                   "Error connecting to payment. Please try again.";
      }
      toast.error(errorMsg);
      logError(e, "handlePayment");
      setCheckingOut(false);
    }
  };

  const nights = dateRange?.from && dateRange?.to 
    ? differenceInDays(dateRange.to, dateRange.from) 
    : 0;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <SEO 
        title="Book Your Stay"
        description="Reserve your stay at Chestnut Hill Inn on Long Island, Maine. Check availability, view pricing, and book directly for the best rates."
        canonical="/booking"
      />
      {/* Header */}
      <header className="bg-white border-b border-stone-200 py-4 px-6 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            data-testid="back-button"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#78716C] hover:text-[#1C1917] transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="hidden sm:inline">Back to Home</span>
          </button>
          <a href="/" className="font-serif text-xl text-[#1C1917]">
            Chestnut Hill Inn
          </a>
          <div className="w-24" />
        </div>
      </header>

      {/* Help Banner */}
      <div className="bg-[#FEF3C7] border-b border-[#FCD34D] py-3 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-sm">
          <span className="text-[#92400E] font-medium flex items-center gap-2">
            <MessageCircle size={16} />
            Need help booking?
          </span>
          <div className="flex items-center gap-4">
            <a href="tel:603-858-2272" className="text-[#92400E] hover:underline flex items-center gap-1">
              <Phone size={14} />
              603-858-2272
            </a>
            <a href="mailto:chestnuthillinn@gmail.com" className="text-[#92400E] hover:underline flex items-center gap-1">
              <Mail size={14} />
              Email Us
            </a>
            <button 
              onClick={() => setShowRequestForm(true)}
              className="bg-[#92400E] text-white px-3 py-1 rounded text-xs font-medium hover:bg-[#78350F]"
            >
              Request to Book
            </button>
          </div>
        </div>
      </div>

      {/* Request to Book Modal */}
      {showRequestForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowRequestForm(false)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            {requestSubmitted ? (
              <div className="text-center py-8">
                <CheckCircle size={48} className="mx-auto text-green-600 mb-4" />
                <h3 className="font-serif text-xl mb-2">Request Received!</h3>
                <p className="text-[#78716C]">We&apos;ll contact you within 24 hours to complete your booking.</p>
                <Button onClick={() => { setShowRequestForm(false); setRequestSubmitted(false); }} className="mt-4">
                  Close
                </Button>
              </div>
            ) : (
              <>
                <h3 className="font-serif text-xl mb-2">Request to Book</h3>
                <p className="text-[#78716C] text-sm mb-4">Having trouble with online booking? Submit your request and we&apos;ll contact you to complete your reservation.</p>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  try {
                    await axios.post(`${API}/booking-request`, {
                      name: formData.get('name'),
                      email: formData.get('email'),
                      phone: formData.get('phone'),
                      dates: formData.get('dates'),
                      guests: formData.get('guests'),
                      message: formData.get('message')
                    });
                    setRequestSubmitted(true);
                  } catch (err) {
                    logError(err, "booking_request_form");
                    toast.error("Failed to submit request. Please call us at 603-858-2272");
                  }
                }} className="space-y-4">
                  <div>
                    <Label htmlFor="req-name">Name *</Label>
                    <Input id="req-name" name="name" required placeholder="Your name" />
                  </div>
                  <div>
                    <Label htmlFor="req-email">Email *</Label>
                    <Input id="req-email" name="email" type="email" required placeholder="your@email.com" />
                  </div>
                  <div>
                    <Label htmlFor="req-phone">Phone *</Label>
                    <Input id="req-phone" name="phone" required placeholder="(555) 123-4567" />
                  </div>
                  <div>
                    <Label htmlFor="req-dates">Desired Dates *</Label>
                    <Input id="req-dates" name="dates" required placeholder="e.g., July 4-11, 2026" />
                  </div>
                  <div>
                    <Label htmlFor="req-guests">Number of Guests</Label>
                    <Input id="req-guests" name="guests" type="number" placeholder="e.g., 8" />
                  </div>
                  <div>
                    <Label htmlFor="req-message">Additional Notes</Label>
                    <Textarea id="req-message" name="message" placeholder="Any special requests or questions..." rows={3} />
                  </div>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={() => setShowRequestForm(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 bg-[#134E4A] hover:bg-[#0F3D3A]">
                      Submit Request
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 sm:gap-4 mb-12">
          {[
            { num: 1, label: "Dates", icon: CalendarDays },
            { num: 2, label: "Details", icon: Users },
            { num: 3, label: "Agreement", icon: FileText },
            { num: 4, label: "Payment", icon: CreditCard },
          ].map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div
                className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-full transition-colors ${
                  step >= s.num
                    ? "bg-[#134E4A] text-white"
                    : "bg-stone-100 text-[#78716C]"
                }`}
              >
                {step > s.num ? (
                  <Check size={16} />
                ) : (
                  <s.icon size={16} />
                )}
                <span className="text-xs sm:text-sm font-medium hidden sm:inline">{s.label}</span>
              </div>
              {i < 3 && (
                <div
                  className={`w-4 sm:w-8 h-px mx-1 sm:mx-2 ${
                    step > s.num ? "bg-[#134E4A]" : "bg-stone-200"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Step 1: Date Selection */}
            {step === 1 && (
              <div data-testid="step-dates" className="bg-white border border-stone-200 p-8">
                <h2 className="font-serif text-3xl text-[#1C1917] mb-2">
                  Select Your Dates
                </h2>
                <p className="text-[#78716C] mb-2">
                  Choose your check-in and check-out dates
                </p>
                <p className="text-sm text-[#D97706] mb-2 font-medium">
                  ⓘ Minimum stay: {(() => {
                    // Seasonal minimum: June-Sept = 4 nights, Oct-May = 2 nights
                    // Exception: First week of June (days 1-7) = 2 nights
                    if (dateRange?.from) {
                      const month = dateRange.from.getMonth() + 1; // getMonth() is 0-indexed
                      const day = dateRange.from.getDate();
                      if (month === 6 && day <= 7) return 2; // First week of June
                      return (month >= 6 && month <= 9) ? 4 : 2;
                    }
                    return 4; // Default to peak season minimum
                  })()} nights
                </p>
                <p className="text-xs text-[#78716C] mb-8">
                  June 8–Sept: 4-night minimum • Oct–May + First week of June: 2-night minimum
                </p>

                <div className="flex justify-center mb-4">
                  <Calendar
                    data-testid="booking-calendar"
                    mode="range"
                    selected={dateRange}
                    onSelect={handleDateSelect}
                    numberOfMonths={2}
                    disabled={(date) => {
                      // Disable past dates
                      if (isBefore(date, addDays(new Date(), -1))) return true;
                      
                      // Disable dates beyond 9 months from today
                      const maxBookingDate = addDays(new Date(), 270); // 9 months = ~270 days
                      if (date > maxBookingDate) return true;
                      
                      return false;
                    }}
                    modifiers={{
                      // Most specific modifiers first for precedence
                      // Late checkout day and early check-in day should ALWAYS be fully red (highest priority)
                      lateCheckoutDay: (date) => isDateLateCheckout(date),
                      earlyCheckinDay: (date) => isDateEarlyCheckin(date),
                      sameDayTurnover: (date) => isDateCheckout(date) && isDateCheckin(date) && !isDateLateCheckout(date) && !isDateEarlyCheckin(date),
                      booked: (date) => isDateBlocked(date) && !isDateEarlyCheckinBuffer(date) && !isDateLateCheckoutBuffer(date) && !isDateEarlyCheckin(date) && !isDateLateCheckout(date),
                      // Buffer days should NOT show if the date is already a late checkout or early check-in day
                      earlyCheckinBuffer: (date) => isDateEarlyCheckinBuffer(date) && !isDateLateCheckout(date) && !isDateEarlyCheckin(date),
                      lateCheckoutBuffer: (date) => isDateLateCheckoutBuffer(date) && !isDateLateCheckout(date) && !isDateEarlyCheckin(date),
                      checkoutDay: (date) => isDateCheckout(date) && !isDateCheckin(date) && !isDateEarlyCheckinBuffer(date) && !isDateLateCheckoutBuffer(date) && !isDateLateCheckout(date),
                      checkinDay: (date) => isDateCheckin(date) && !isDateCheckout(date) && !isDateEarlyCheckinBuffer(date) && !isDateLateCheckoutBuffer(date) && !isDateEarlyCheckin(date),
                      available: (date) => !isDateBlocked(date) && !isDateEarlyCheckinBuffer(date) && !isDateLateCheckoutBuffer(date) && !isDateCheckout(date) && !isDateCheckin(date) && !isBefore(date, addDays(new Date(), -1)),
                    }}
                    modifiersStyles={{
                      booked: {
                        backgroundColor: "#FEE2E2",
                        color: "#991B1B",
                        fontWeight: "500",
                      },
                      sameDayTurnover: {
                        backgroundColor: "#FEE2E2",
                        color: "#991B1B",
                        fontWeight: "500",
                      },
                      earlyCheckinBuffer: {
                        background: "linear-gradient(135deg, #DCFCE7 50%, #FEE2E2 50%)",
                        color: "#1F2937",
                        fontWeight: "500",
                        border: "2px solid #F59E0B",
                        borderRadius: "4px",
                      },
                      lateCheckoutBuffer: {
                        background: "linear-gradient(135deg, #FEE2E2 50%, #DCFCE7 50%)",
                        color: "#1F2937",
                        fontWeight: "500",
                        border: "2px solid #F59E0B",
                        borderRadius: "4px",
                      },
                      earlyCheckinDay: {
                        backgroundColor: "#FEE2E2",
                        color: "#991B1B",
                        fontWeight: "500",
                        border: "none",
                        borderRadius: "0",
                      },
                      lateCheckoutDay: {
                        backgroundColor: "#FEE2E2",
                        color: "#991B1B",
                        fontWeight: "500",
                        border: "none",
                        borderRadius: "0",
                      },
                      checkoutDay: {
                        background: "linear-gradient(135deg, #FEE2E2 50%, #DCFCE7 50%)",
                        color: "#1F2937",
                        fontWeight: "500",
                      },
                      checkinDay: {
                        background: "linear-gradient(135deg, #DCFCE7 50%, #FEE2E2 50%)",
                        color: "#1F2937",
                        fontWeight: "500",
                      },
                      available: {
                        backgroundColor: "#DCFCE7",
                        color: "#166534",
                      },
                    }}
                    className="rounded-none border-0"
                  />
                </div>
                
                {/* Calendar legend */}
                <div className="flex flex-wrap justify-center gap-4 text-xs text-[#78716C] mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{backgroundColor: "#DCFCE7", border: "1px solid #166534"}}></div>
                    <span>Available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{backgroundColor: "#FEE2E2", border: "1px solid #991B1B"}}></div>
                    <span>Booked</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{background: "linear-gradient(135deg, #DCFCE7 50%, #FEE2E2 50%)", border: "2px solid #F59E0B"}}></div>
                    <span>Buffer - Early check-in available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{background: "linear-gradient(135deg, #FEE2E2 50%, #DCFCE7 50%)", border: "2px solid #F59E0B"}}></div>
                    <span>Buffer - Late check-out available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{background: "linear-gradient(135deg, #FEE2E2 50%, #DCFCE7 50%)", border: "1px solid #78716C"}}></div>
                    <span>Check-in/out day</span>
                  </div>
                </div>
                
                {/* Selected dates summary - prominent display */}
                {dateRange?.from && dateRange?.to && (
                  <div className="bg-[#E6F2F1] border border-[#134E4A]/20 rounded-lg p-4 text-center">
                    <div className="text-lg font-serif text-[#134E4A]">
                      {format(dateRange.from, "MMMM d, yyyy")} → {format(dateRange.to, "MMMM d, yyyy")}
                    </div>
                    <div className="text-2xl font-bold text-[#134E4A] mt-1">
                      {differenceInDays(dateRange.to, dateRange.from)} nights
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Guest Information */}
            {step === 2 && (
              <div data-testid="step-details" className="bg-white border border-stone-200 p-8">
                <h2 className="font-serif text-3xl text-[#1C1917] mb-2">
                  Guest Information
                </h2>
                <p className="text-[#78716C] mb-8">
                  Tell us about your group
                </p>

                <div className="space-y-6">
                  <div>
                    <Label className="text-sm uppercase tracking-widest text-[#78716C] mb-2 block">
                      Number of Guests
                    </Label>
                    <div className="flex items-center gap-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
                        className="w-12 h-12 text-xl"
                      >
                        -
                      </Button>
                      <span className="text-2xl font-serif w-12 text-center">
                        {guestCount}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          setGuestCount(Math.min(14, guestCount + 1))
                        }
                        className="w-12 h-12 text-xl"
                      >
                        +
                      </Button>
                    </div>
                    <div className="mt-3 text-sm text-[#78716C]">
                      <p>Up to <strong>14 guests</strong> included in base rate.</p>
                      <p className="text-xs mt-1">
                        Need more than 14? Additional guests require approval and can be added via the{" "}
                        <span className="text-[#134E4A] font-medium">Add-Ons section</span> in the sidebar.
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-sm uppercase tracking-widest text-[#78716C] mb-2 block">
                        Full Name *
                      </Label>
                      <Input
                        data-testid="guest-name"
                        value={guestInfo.name}
                        onChange={(e) =>
                          setGuestInfo({ ...guestInfo, name: e.target.value })
                        }
                        placeholder="John Smith"
                        className="border-0 border-b border-stone-300 rounded-none px-0 py-4 focus:ring-0 focus:border-[#134E4A]"
                      />
                    </div>
                    <div>
                      <Label className="text-sm uppercase tracking-widest text-[#78716C] mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        data-testid="guest-email"
                        type="email"
                        value={guestInfo.email}
                        onChange={(e) =>
                          setGuestInfo({ ...guestInfo, email: e.target.value })
                        }
                        placeholder="john@example.com"
                        className="border-0 border-b border-stone-300 rounded-none px-0 py-4 focus:ring-0 focus:border-[#134E4A]"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm uppercase tracking-widest text-[#78716C] mb-2 block">
                      Phone Number *
                    </Label>
                    <Input
                      data-testid="guest-phone"
                      type="tel"
                      value={guestInfo.phone}
                      onChange={(e) =>
                        setGuestInfo({ ...guestInfo, phone: e.target.value })
                      }
                      placeholder="(555) 123-4567"
                      className="border-0 border-b border-stone-300 rounded-none px-0 py-4 focus:ring-0 focus:border-[#134E4A]"
                    />
                  </div>

                  <div>
                    <Label className="text-sm uppercase tracking-widest text-[#78716C] mb-2 block">
                      Special Requests
                    </Label>
                    <Textarea
                      data-testid="special-requests"
                      value={guestInfo.specialRequests}
                      onChange={(e) =>
                        setGuestInfo({ ...guestInfo, specialRequests: e.target.value })
                      }
                      placeholder="Early check-in, celebration arrangements, dietary requirements..."
                      className="border border-stone-300 rounded-none min-h-24 focus:ring-0 focus:border-[#134E4A]"
                    />
                  </div>

                  {/* Email Marketing Opt-in */}
                  <div className="border-t border-stone-100 pt-6">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="marketing-opt-in"
                        checked={guestInfo.marketingOptIn}
                        onCheckedChange={(checked) =>
                          setGuestInfo({ ...guestInfo, marketingOptIn: checked })
                        }
                        className="mt-1"
                      />
                      <label htmlFor="marketing-opt-in" className="text-sm text-[#78716C] cursor-pointer">
                        <span className="flex items-center gap-2 text-[#1C1917] font-medium mb-1">
                          <Mail size={16} className="text-[#134E4A]" />
                          Stay in touch
                        </span>
                        I&apos;d like to receive occasional updates about special offers, seasonal availability, and news from Chestnut Hill Inn. (We respect your inbox - typically 1-2 emails per month)
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between mt-8">
                  <Button
                    variant="outline"
                    onClick={() => { setStep(1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="px-8 py-6 text-sm uppercase tracking-widest"
                  >
                    Back
                  </Button>
                  <Button
                    data-testid="continue-to-agreement"
                    onClick={() => {
                      if (!guestInfo.name || !guestInfo.email || !guestInfo.phone) {
                        toast.error("Please fill in all required fields");
                        return;
                      }
                      // Confirm guest count if it's still at default (2) for a large property
                      if (guestCount <= 2) {
                        const confirmed = window.confirm(
                          `You've indicated ${guestCount} guest(s) for your stay.\n\nChestnut Hill Inn accommodates up to 14 guests. Please confirm this is correct, as the guest count will appear on your rental agreement.\n\nClick OK if ${guestCount} guest(s) is correct, or Cancel to go back and update.`
                        );
                        if (!confirmed) {
                          return;
                        }
                      }
                      setStep(3);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white px-8 py-6 text-sm uppercase tracking-widest"
                  >
                    Continue to Agreement
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Rental Agreement */}
            {step === 3 && (
              <div data-testid="step-agreement">
                <RentalAgreement
                  bookingId={booking?.id || "pending"}
                  guestName={guestInfo.name}
                  bookingDetails={{
                    check_in: dateRange.from ? format(dateRange.from, "MMMM d, yyyy") : "TBD",
                    check_out: dateRange.to ? format(dateRange.to, "MMMM d, yyyy") : "TBD",
                    guest_count: guestCount,
                    nightly_rate: pricing?.subtotal / pricing?.total_nights || 800,
                    cleaning_fee: pricing?.cleaning_fee || 350,
                    golf_cart_gas_fee: pricing?.golf_cart_gas_fee || 50,
                    taxes: pricing?.taxes,
                    total_price: pricing?.total_price,
                    protection_amount: pricing?.protection_amount,
                    selected_addons: Object.entries(selectedAddons)
                      .filter(([_, selected]) => selected)
                      .map(([addonId, _]) => {
                        const addon = addons.find(a => a.id === addonId);
                        const qty = addonQuantities[addonId] || 1;
                        const isPerNight = addon?.type === 'fee' && addon?.name?.toLowerCase().includes('extra guest');
                        return { 
                          id: addonId, 
                          name: addon?.name || addonId,
                          type: addon?.type,
                          price: addon?.price || 0,
                          quantity: qty,
                          nights: isPerNight ? nights : 1,
                          total: (addon?.price || 0) * qty * (isPerNight ? nights : 1)
                        };
                      }),
                    addons_total: Object.entries(selectedAddons)
                      .filter(([_, selected]) => selected)
                      .reduce((sum, [addonId, _]) => {
                        const addon = addons.find(a => a.id === addonId);
                        const qty = addonQuantities[addonId] || 1;
                        const isPerNight = addon?.type === 'fee' && addon?.name?.toLowerCase().includes('extra guest');
                        return sum + ((addon?.price || 0) * qty * (isPerNight ? nights : 1));
                      }, 0),
                  }}
                  onAccept={async (signatureValue) => {
                    setAgreementSigned(true);
                    // Now create the booking
                    setLoading(true);
                    try {
                      const res = await axios.post(`${API}/bookings`, {
                        check_in: format(dateRange.from, "yyyy-MM-dd"),
                        check_out: format(dateRange.to, "yyyy-MM-dd"),
                        guest_count: guestCount,
                        guest_name: guestInfo.name,
                        guest_email: guestInfo.email,
                        guest_phone: guestInfo.phone,
                        special_requests: guestInfo.specialRequests || null,
                        protection_type: protectionType,
                        payment_type: pricing?.requires_split ? "split" : "full",
                        payment_method: paymentMethod,
                        discount_code: discountApplied ? discountCode : null,
                        marketing_opt_in: guestInfo.marketingOptIn,
                        selected_addons: Object.entries(selectedAddons)
                          .filter(([_, selected]) => selected)
                          .map(([addonId, _]) => addonId),
                        addon_quantities: addonQuantities,
                      });
                      setBooking(res.data);
                      
                      // Save agreement signature to the booking
                      await axios.post(`${API}/bookings/${res.data.id}/agreement`, {
                        signature: signatureValue || guestInfo.name,
                        agreed_to_rules: true,
                        signed_at: new Date().toISOString(),
                      });
                      
                      setStep(4);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      toast.success("Booking created! Proceed to payment.");
                    } catch (e) {
                      const isTimeout = e.code === 'ECONNABORTED' || e.message?.includes('timeout');
                      const isNetworkError = e.message === 'Network Error' || !e.response;
                      
                      let errorMsg;
                      if (isTimeout) {
                        errorMsg = "Request timed out. Please check your internet connection and try again.";
                      } else if (isNetworkError) {
                        errorMsg = "Unable to connect to server. Please check your internet and try again, or use 'Request to Book' below.";
                      } else {
                        const errorDetail = e.response?.data?.detail;
                        errorMsg = typeof errorDetail === 'string' ? errorDetail : 
                                   Array.isArray(errorDetail) ? errorDetail[0]?.msg || "Error creating booking" :
                                   "Error creating booking. Please try again or use 'Request to Book' below.";
                      }
                      toast.error(errorMsg);
                      logError(e, "onAccept_createBookingWithAgreement");
                    } finally {
                      setLoading(false);
                    }
                  }}
                />
                <div className="mt-4">
                  <Button
                    variant="outline"
                    onClick={() => { setStep(2); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="px-8 py-6 text-sm uppercase tracking-widest"
                  >
                    Back to Guest Details
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Payment */}
            {step === 4 && booking && (
              <div data-testid="step-payment" className="bg-white border border-stone-200 p-8">
                <h2 className="font-serif text-2xl text-[#1C1917] mb-2">Confirm & Pay</h2>
                <p className="text-[#78716C] mb-8">
                  Review your booking details and payment schedule.
                </p>

                {/* Payment Schedule Breakdown - Always show */}
                <div className="bg-[#e6f2f1] border border-[#134E4A] p-6 mb-8">
                  <h3 className="font-serif text-lg text-[#134E4A] mb-4 flex items-center gap-2">
                    <CreditCard size={20} />
                    Payment Schedule
                  </h3>
                  
                  {/* Total Trip Cost - excluding security deposit */}
                  <div className="flex justify-between py-2 border-b border-[#134E4A]/20 mb-3">
                    <span className="font-medium text-[#1C1917]">Total Trip Cost</span>
                    <span className="font-bold text-[#1C1917]">
                      ${((booking.total_with_protection || pricing?.total_with_protection || 0) - (booking.protection_amount || pricing?.protection_amount || 0)).toLocaleString()}
                    </span>
                  </div>
                  
                  {/* Security Deposit Note */}
                  <div className="flex justify-between py-2 border-b border-[#134E4A]/20 mb-3 text-sm">
                    <div>
                      <span className="text-[#78716C]">Security Deposit</span>
                      <p className="text-xs text-[#78716C]">Card HOLD only - not charged</p>
                    </div>
                    <span className="text-[#78716C]">${(booking.protection_amount || pricing?.protection_amount || 0).toLocaleString()}</span>
                  </div>
                  
                  {/* Payment 1 - Due Now */}
                  <div className="flex justify-between py-3 bg-[#134E4A] text-white px-3 -mx-3 mb-2">
                    <div>
                      <span className="font-medium">Payment 1 - Due Now</span>
                      <p className="text-xs text-white/80">Non-refundable deposit to secure dates</p>
                    </div>
                    <span className="font-bold text-lg">${(booking.first_payment || pricing?.first_payment || 1000).toLocaleString()}</span>
                  </div>
                  
                  {/* Payment 2 */}
                  {pricing?.second_payment > 0 && (
                    <div className="flex justify-between py-2 border-b border-[#134E4A]/20">
                      <div>
                        <span className="text-[#1C1917]">Payment 2</span>
                        <p className="text-xs text-[#78716C]">Due: {pricing?.second_payment_due ? new Date(pricing.second_payment_due).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '2 months before arrival'}</p>
                      </div>
                      <span className="text-[#1C1917] font-medium">${pricing?.second_payment?.toLocaleString()}</span>
                    </div>
                  )}
                  
                  {/* Payment 3 */}
                  {pricing?.third_payment > 0 && (
                    <div className="flex justify-between py-2 border-b border-[#134E4A]/20">
                      <div>
                        <span className="text-[#1C1917]">Payment 3</span>
                        <p className="text-xs text-[#78716C]">Due: {pricing?.third_payment_due ? new Date(pricing.third_payment_due).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '1 month before arrival'}</p>
                      </div>
                      <span className="text-[#1C1917] font-medium">${pricing?.third_payment?.toLocaleString()}</span>
                    </div>
                  )}
                  
                  {/* Short notice booking info */}
                  {pricing?.payment_schedule === 'full' && (
                    <div className="mt-3 text-xs text-amber-700 bg-amber-50 p-2 rounded">
                      <strong>Short Notice Booking:</strong> Full payment required as your check-in is within 30 days.
                    </div>
                  )}
                  {pricing?.payment_schedule === 'short_2month' && (
                    <div className="mt-3 text-xs text-amber-700 bg-amber-50 p-2 rounded">
                      <strong>Notice:</strong> Deposit + 2nd payment required now as check-in is within 60 days.
                    </div>
                  )}
                  
                  {/* Alternative payment methods note */}
                  <div className="mt-4 text-xs text-[#78716C] border-t border-[#134E4A]/20 pt-3">
                    <strong>💳 Card saved on file.</strong> You can change to Venmo, Zelle, or PayPal for future payments via your Guest Portal.
                  </div>
                </div>

                {/* Important Notice */}
                <div className="bg-amber-50 border border-amber-200 p-4 mb-8 flex items-start gap-3">
                  <Shield className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Your dates will be blocked immediately upon payment.</p>
                    <p className="text-xs text-amber-700 mt-1">The $1,000 deposit is non-refundable. See our cancellation policy in the rental agreement.</p>
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex justify-between py-3 border-b border-stone-100">
                    <span className="text-[#78716C]">Guest</span>
                    <span className="text-[#1C1917] font-medium">{booking.guest_name}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-stone-100">
                    <span className="text-[#78716C]">Email</span>
                    <span className="text-[#1C1917]">{booking.guest_email}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-stone-100">
                    <span className="text-[#78716C]">Dates</span>
                    <span className="text-[#1C1917]">
                      {booking.check_in} to {booking.check_out}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-stone-100">
                    <span className="text-[#78716C]">Guests</span>
                    <span className="text-[#1C1917]">{booking.guest_count}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-stone-100">
                    <span className="text-[#78716C]">Protection</span>
                    <span className="text-[#1C1917]">
                      {booking.protection_type === "deposit" ? "Security Deposit (Refundable)" : "Security Deposit (Refundable)"}
                    </span>
                  </div>
                  {booking.special_requests && (
                    <div className="py-3 border-b border-stone-100">
                      <span className="text-[#78716C] block mb-2">Special Requests</span>
                      <span className="text-[#1C1917]">{booking.special_requests}</span>
                    </div>
                  )}
                </div>

                {/* Pay Button - Always Stripe, always first_payment amount */}
                <Button
                  data-testid="pay-now-button"
                  onClick={() => handlePayment(1)}
                  disabled={checkingOut}
                  className="w-full bg-[#134E4A] hover:bg-[#0f3d3a] text-white py-6 text-lg uppercase tracking-widest"
                >
                  {checkingOut ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Redirecting to Payment...
                    </>
                  ) : (
                    `Pay $${(pricing?.first_payment || 1000).toLocaleString()} Deposit`
                  )}
                </Button>
                <p className="text-center text-[#78716C] text-sm mt-4">
                  Secure payment powered by Stripe • Card will be saved for future payments
                </p>
              </div>
            )}

          </div>

          {/* Sidebar - Booking Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-stone-200 p-6 sticky top-24">
              <div className="aspect-video overflow-hidden mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_bookmate-32/artifacts/dt1aqqgm_20200713182806961693000000-o.jpg"
                  alt="Chestnut Hill Inn"
                  className="w-full h-full object-cover"
                />
              </div>

              <h3 className="font-serif text-xl text-[#1C1917] mb-1">
                Chestnut Hill Inn
              </h3>
              <p className="text-[#78716C] text-sm mb-6">
                Long Island, Maine
              </p>

              <div className="border-t border-stone-100 pt-6 space-y-4">
                {dateRange?.from && dateRange?.to && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#78716C]">Check-in</span>
                      <span className="text-[#1C1917]">
                        {format(dateRange.from, "MMM d, yyyy")}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#78716C]">Check-out</span>
                      <span className="text-[#1C1917]">
                        {format(dateRange.to, "MMM d, yyyy")}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#78716C]">Nights</span>
                      <span className="text-[#1C1917]">{nights}</span>
                    </div>
                  </>
                )}

                {(pricing || booking) && (
                  <>
                    <div className="border-t border-stone-100 pt-4 mt-4">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-[#78716C]">
                          ${Math.round((booking?.subtotal || pricing?.subtotal) / (booking?.total_nights || pricing?.total_nights || 1))}/night x {booking?.total_nights || pricing?.total_nights} nights
                        </span>
                        <span className="text-[#1C1917]">
                          ${(booking?.subtotal || pricing?.subtotal)?.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-[#78716C]">Cleaning fee</span>
                        <span className="text-[#1C1917]">
                          ${(booking?.cleaning_fee || pricing?.cleaning_fee)?.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-[#78716C]">4-Seater Golf Cart (included)</span>
                        <span className="text-[#1C1917]">$0</span>
                      </div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-[#78716C]">Golf Cart Gas Fee</span>
                        <span className="text-[#1C1917]">
                          ${(booking?.golf_cart_gas_fee || pricing?.golf_cart_gas_fee || 50)?.toLocaleString()}
                        </span>
                      </div>
                      {(pricing?.discount_applied || booking?.discount_amount > 0) && (
                        <div className="flex justify-between text-sm mb-2 text-green-600">
                          <span>Discount ({((pricing?.discount_percent || 0) * 100).toFixed(0)}% off)</span>
                          <span>-${(booking?.discount_amount || pricing?.discount_amount)?.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-[#78716C]">Maine rental tax (9%)</span>
                        <span className="text-[#1C1917]">
                          ${(booking?.taxes || pricing?.taxes)?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    
                    {/* Property Protection - Security Deposit Only */}
                    {!booking && (
                      <div className="border-t border-stone-100 pt-4 mt-4">
                        <Label className="text-xs uppercase tracking-widest text-[#78716C] mb-3 block">
                          Property Protection
                        </Label>
                        <div className="flex items-start space-x-3 p-3 border border-[#134E4A] bg-[#e6f2f1]">
                          <Shield size={20} className="text-[#134E4A] mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-[#1C1917]">
                              Security Deposit
                            </p>
                            <p className="text-xs text-[#78716C] mt-1">
                              ${pricing?.security_deposit || 500} refundable • Returned 7 days after checkout if no damages
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Add-ons - Only show before booking created */}
                    {!booking && addons.length > 0 && (
                      <div className="border-t border-stone-100 pt-4 mt-4">
                        <Label className="text-xs uppercase tracking-widest text-[#78716C] mb-3 block">
                          Optional Add-ons
                        </Label>
                        <div className="space-y-3">
                          {addons.map((addon) => {
                            const isAvailable = addon.type === 'early_checkin' 
                              ? addonAvailability?.early_checkin?.available === true
                              : addon.type === 'late_checkout'
                              ? addonAvailability?.late_checkout?.available === true
                              : true;
                            
                            // Check if this addon supports quantity (fee type like extra guests)
                            const supportsQuantity = addon.type === 'fee' && addon.name.toLowerCase().includes('extra guest');
                            const quantity = addonQuantities[addon.id] || 1;
                            
                            return (
                              <div 
                                key={addon.id} 
                                className={`flex items-start space-x-3 p-3 border ${
                                  !isAvailable ? 'opacity-50 bg-stone-50' : 
                                  selectedAddons[addon.id] ? 'border-[#134E4A] bg-[#e6f2f1]' : 'border-stone-200'
                                }`}
                              >
                                <Checkbox
                                  id={addon.id}
                                  checked={selectedAddons[addon.id] || false}
                                  onCheckedChange={(checked) => {
                                    if (isAvailable) {
                                      setSelectedAddons(prev => ({...prev, [addon.id]: checked}));
                                      if (!checked) {
                                        setAddonQuantities(prev => ({...prev, [addon.id]: 1}));
                                      }
                                    }
                                  }}
                                  disabled={!isAvailable}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <label htmlFor={addon.id} className={`text-sm font-medium text-[#1C1917] flex items-center gap-2 ${!isAvailable ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                    <Clock size={16} className="text-[#134E4A]" />
                                    {addon.name} (+${supportsQuantity && selectedAddons[addon.id] ? (addon.price * quantity).toFixed(0) : addon.price}{supportsQuantity ? '/person/night' : ''})
                                  </label>
                                  <p className="text-xs text-[#78716C] mt-1">
                                    {addon.description}
                                  </p>
                                  {/* Quantity selector for extra guests */}
                                  {supportsQuantity && selectedAddons[addon.id] && (
                                    <div className="flex items-center gap-2 mt-2">
                                      <span className="text-xs text-[#78716C]">Number of extra guests:</span>
                                      <select 
                                        value={quantity}
                                        onChange={(e) => setAddonQuantities(prev => ({...prev, [addon.id]: parseInt(e.target.value)}))}
                                        className="text-sm border border-stone-200 rounded px-2 py-1"
                                      >
                                        {[1,2,3,4,5,6,7,8,9,10].map(n => (
                                          <option key={n} value={n}>{n}</option>
                                        ))}
                                      </select>
                                      <span className="text-xs text-[#134E4A] font-medium">
                                        = ${(addon.price * quantity * nights).toFixed(0)} total
                                      </span>
                                    </div>
                                  )}
                                  {!isAvailable && (
                                    <p className="text-xs text-amber-600 mt-1">
                                      {addon.type === 'early_checkin' 
                                        ? addonAvailability?.early_checkin?.reason || 'Another guest is checking out on your arrival day'
                                        : addon.type === 'late_checkout'
                                        ? addonAvailability?.late_checkout?.reason || 'Another guest is checking in on your departure day'
                                        : 'Not available for selected dates'}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Payment Method Selection - Only show before booking */}
                    {/* FIRST PAYMENT MUST BE CREDIT CARD ONLY - Alternative methods for 2nd/3rd payments */}
                    {!booking && paymentMethods.length > 0 && (
                      <div className="border-t border-stone-100 pt-4 mt-4">
                        <Label className="text-xs uppercase tracking-widest text-[#78716C] mb-3 block">
                          Payment Method
                        </Label>
                        
                        {/* Credit Card Only for Initial Booking */}
                        <div 
                          className="flex items-start space-x-3 p-3 border border-[#134E4A] bg-[#e6f2f1] mb-4"
                        >
                          <CreditCard size={20} className="text-[#134E4A] mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-[#1C1917]">Credit/Debit Card</p>
                            <p className="text-xs text-[#78716C] mt-1">Required for initial $1,000 deposit to secure your dates</p>
                          </div>
                        </div>
                        
                        {/* Info about alternative payment methods */}
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                          <p className="text-xs text-blue-800">
                            <strong>Note:</strong> Venmo, Zelle, PayPal, and Cash App are available for your 2nd and 3rd payments. 
                            You can change your payment preference in the Guest Portal after booking.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Show selected add-ons */}
                    {!booking && addons.filter(a => selectedAddons[a.id]).length > 0 && (
                      <div className="border-t border-stone-100 pt-4">
                        {addons.filter(a => selectedAddons[a.id]).map(addon => {
                          const qty = addonQuantities[addon.id] || 1;
                          const isPerNight = addon.type === 'fee' && addon.name.toLowerCase().includes('extra guest');
                          const total = addon.price * qty * (isPerNight ? nights : 1);
                          return (
                            <div key={addon.id} className="flex justify-between text-sm mb-2">
                              <span className="text-[#78716C]">
                                {addon.name}{qty > 1 ? ` (×${qty})` : ''}{isPerNight ? ` × ${nights} nights` : ''}
                              </span>
                              <span className="text-[#1C1917]">${total.toLocaleString()}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* TRIP TOTAL - Without Security Deposit */}
                    <div className="border-t border-stone-100 pt-4 mt-4">
                      <div className="flex justify-between">
                        <span className="text-[#1C1917] font-semibold">Trip Total</span>
                        <span className="font-serif text-2xl text-[#134E4A]">
                          ${(() => {
                            // If we have a booking, use total_with_protection minus security deposit
                            if (booking) {
                              const totalWithAddons = booking.total_with_protection || 0;
                              const securityDeposit = booking.protection_amount || 0;
                              return (totalWithAddons - securityDeposit).toLocaleString();
                            }
                            // Otherwise calculate from pricing + selected addons
                            const baseTotal = pricing?.total_price || 0;
                            const addonsTotal = addons.filter(a => selectedAddons[a.id]).reduce((sum, a) => {
                              const qty = addonQuantities[a.id] || 1;
                              const isPerNight = a.type === 'fee' && a.name.toLowerCase().includes('extra guest');
                              return sum + (a.price * qty * (isPerNight ? nights : 1));
                            }, 0);
                            return (baseTotal + addonsTotal).toLocaleString();
                          })()}
                        </span>
                      </div>
                      <p className="text-xs text-[#78716C] mt-1">Includes accommodations, cleaning fee, add-ons, and taxes</p>
                    </div>

                    {/* SECURITY DEPOSIT - Separate Section */}
                    <div className="border-t border-stone-100 pt-4 mt-4 bg-amber-50 -mx-6 px-6 py-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="text-[#92400E] font-medium text-sm">Security Deposit</span>
                          <span className="ml-2 text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">HOLD ONLY</span>
                        </div>
                        <span className="text-[#92400E] font-medium">
                          ${(booking?.protection_amount || pricing?.protection_amount)?.toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs text-[#92400E] mt-2">
                        <strong>Not charged.</strong> A temporary hold is placed on your card 7 days before arrival and automatically released within 7 days after checkout if no damage or excessive cleaning is needed.
                      </p>
                    </div>

                    {/* Split Payment Notice */}
                    {(pricing?.requires_split || booking?.payment_type === "split") && (
                      <div className="border-t border-stone-100 pt-4 mt-4 bg-amber-50 p-3 -mx-3">
                        <p className="text-xs font-medium text-amber-800 mb-1">Split Payment Required</p>
                        <p className="text-xs text-amber-700">
                          50% due now (${(booking?.first_payment || pricing?.first_payment)?.toLocaleString()}), 
                          50% due 30 days before check-in
                        </p>
                      </div>
                    )}
                    {/* Discount Code Input */}
                    {!booking && !discountApplied && (
                      <div className="border-t border-stone-100 pt-4 mt-4">
                        <Label className="text-xs uppercase tracking-widest text-[#78716C] mb-2 block">
                          Have a discount code?
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            value={discountCode}
                            onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                            placeholder="Enter code"
                            className="flex-1"
                          />
                          <Button variant="outline" onClick={handleApplyDiscount} className="px-4">
                            Apply
                          </Button>
                        </div>
                      </div>
                    )}
                    {discountApplied && (
                      <div className="border-t border-stone-100 pt-4 mt-4">
                        <p className="text-sm text-green-600 flex items-center gap-2">
                          <CheckCircle size={16} /> Code {discountCode} applied!
                        </p>
                      </div>
                    )}
                    
                    {/* Continue Button - Show on Step 1 when dates selected */}
                    {step === 1 && dateRange?.from && dateRange?.to && (
                      <div className="border-t border-stone-100 pt-4 mt-4">
                        <Button
                          data-testid="continue-to-details"
                          onClick={handleContinueToGuests}
                          className="w-full bg-[#134E4A] hover:bg-[#0f3d3a] text-white py-6 text-sm uppercase tracking-widest"
                        >
                          Continue
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
